// src/pages/LoginPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Flame, User, Lock, LogIn } from 'lucide-react';
import background from "../assets/img/bg.png";

function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASEURL}/login`, {
        username,
        password,
      });

      if (response.status === 200) {
        toast.success('Login successful!');
        setTimeout(() => {
          onLogin();
          navigate('/dashboard');
        }, 1000);
      }
    } catch (error) {
      toast.error(error.response?.data.message || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-amber-100"
    style={{ backgroundImage: `url(${background})`
    }}>
      {/* Decorative background elements */}
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="absolute w-32 h-32 rounded-full top-20 left-20 bg-amber-500 blur-xl"></div>
        <div className="absolute w-32 h-32 bg-orange-500 rounded-full bottom-20 right-20 blur-xl"></div>
        <div className="absolute w-64 h-64 transform -translate-x-1/2 -translate-y-1/2 rounded-full top-1/2 left-1/2 bg-amber-300 blur-2xl"></div>
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4">
        {/* Logo and Heading */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 rounded-full shadow-lg bg-gradient-to-br from-amber-500 to-amber-700">
              <Flame size={40} className="text-white" />
            </div>
          </div>
          <h1 className="mb-2 text-4xl font-extrabold tracking-tight text-amber-900">
            Mr WoodFire
          </h1>
          <p className="text-amber-700">Admin Dashboard</p>
        </div>

        {/* Login Form */}
        <div className="w-full max-w-md">
          <div className="p-8 border shadow-xl bg-white/80 backdrop-blur-lg rounded-2xl border-amber-100">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block mb-1 text-sm font-medium text-amber-900">
                  <div className="flex items-center">
                    <User size={16} className="mr-2" />
                    Username
                  </div>
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  required
                  className="w-full px-4 py-2 transition-all duration-200 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 placeholder:text-amber-300"
                />
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-amber-900">
                  <div className="flex items-center">
                    <Lock size={16} className="mr-2" />
                    Password
                  </div>
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full px-4 py-2 transition-all duration-200 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 placeholder:text-amber-300"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center justify-center w-full px-4 py-3 font-medium text-white transition-all duration-200 rounded-lg bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-3 -ml-1 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <LogIn size={18} className="mr-2" />
                    Sign In
                  </div>
                )}
              </button>
            </form>
          </div>

          {/* Footer */}
          <p className="mt-8 text-sm text-center text-amber-700">
          Copyright © 2024 Mr Woodfire by VEXIM®
          </p>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}

export default LoginPage;